import { Button, Col, Dropdown, Input, InputNumber, Menu, Popconfirm, Row, Select, Switch, Tooltip } from "antd";
import { DefaultOptionType } from "antd/lib/select";
import { useMemo, useState } from "react";
import appConfig from "../../../appConfig";
import Icon from "../../../assets/icons/icon";
import InputNum from "../../../components/form/inputNum";
import DividerLine from "../../../components/lineSplit";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import { useTaxLegalId } from "../../../contexts/taxLegalCaseStore";
import { ACCEPTED_ID, DRAFT_ID, OFFERED_ID, TaxLegalReceivablesStore } from "../../../contexts/taxLegalReceivablesStore";
import _t from "../../../lang/translate";
import { acceptReceivable, deleteReceivable, deleteReceivableTemplate, publishReceivable, receivableProductsUrl, receivableTemplatesUrl, storeReceivable, storeReceivableTemplate, updateReceivableTemplate, UpsertReceivableTemplatePayload } from "../../../services/taxLegalService";
import { useApi } from "../../../services/useApi";
import { PaginationedData } from "../../../types/apiTypes";
import { OfferDraft, OfferLine, OfferLineId, OfferTextLine, OfferTotals, Receivable, ReceivableId, ReceivableProduct, ReceivableTemplate } from "../../../types/taxLegalTypes";
import format from "../../../utilities/formatNumbers";
import ServerSelectBox from "../../../components/serverSelectBox";
import { paymentTermsAutoComplete } from "../../../services/autocompleteService";
import { isValidNum } from "../../../utilities/typeGuard";
import { openFileLink } from "../../../services/mediaService";

const EMPTY_TEMPLATE_ID = -1
const PAYMENT_TERM_NETTO_8_DAYS_ID = 1;
const emptyTemplate: OfferDraft = {
  paymentTermsId: undefined,
  title: undefined,
  itemLines: [
    { id: 1 },
  ],
  textLines: [
    { id: 1, description: "" },
  ]
}
export default function OfferForm() {
  const taxLegalCaseId = useTaxLegalId()
  const { offers, addReceivable, replaceReceivable, removeReceivable } = TaxLegalReceivablesStore.useContainer()
  const [{ data: templates, isLoading: isLoadingTemplates }, _setUrl, setTemplatesData] = useApi<PaginationedData<ReceivableTemplate>>(receivableTemplatesUrl, { data: [] })
  const [{ data: { data: receivableProducts }, isLoading: isLoadingProducts }] = useApi<{ data: ReceivableProduct[] }>(receivableProductsUrl, { data: [] })
  const [draft, setDraft] = useState<OfferDraft>(emptyTemplate)
  const [loadingIds, setLoadingIds] = useState<Set<string | number>>(new Set())

  const [templateId, setTemplateId] = useState<ReceivableId | undefined>(EMPTY_TEMPLATE_ID)
  const items = draft.itemLines;
  const descriptionLines = draft.textLines

  const priceResult = useMemo(() => calculatePrice(items ?? [], receivableProducts ?? []), [items, receivableProducts])
  const templateOptions = useMemo(() => {
    return [
      ...templates.data.map(item => ({ value: item.id, label: item.title })),
      { value: EMPTY_TEMPLATE_ID, label: "-- Ny skabelon --" }
    ]
  }, [templates])
  const productOptions = useMemo(() => {
    return receivableProducts?.map(item => ({ value: item.code, label: `${item.code} - ${item.title}` }))
  }, [receivableProducts])
  function toggleLoading(id: string | number, loading: boolean) {
    setLoadingIds((prev) => {
      const newSet = new Set(prev);
      if (loading) {
        newSet.add(id)
      } else {
        newSet.delete(id)
      }
      return newSet
    })
  }
  function setItems(action: React.SetStateAction<OfferLine[]>) {
    setDraft((prev) => {
      const newItems = typeof action === 'function' ? action(prev.itemLines ?? []) : action
      return {
        ...prev,
        itemLines: newItems
      }
    })
  }
  function setTitle(title: string) {
    setDraft((prev) => ({ ...prev, title }))
  }

  function handleSetTemplate(templateId: number | undefined) {
    setTemplateId(templateId)
    if (!templateId) return
    const formTemplate = templateId === EMPTY_TEMPLATE_ID
      ? emptyTemplate
      : templates?.data?.find(item => item.id === templateId)
    if (!formTemplate) return;
    setDraft({ paymentTermsId: PAYMENT_TERM_NETTO_8_DAYS_ID, ...formTemplate })
  }

  async function saveOffer(send: boolean) {
    try {
      toggleLoading('sending_offer', true)
      let newReceivable: Receivable;
      const { data: storedReceivable } = await storeReceivable(taxLegalCaseId, {
        itemLines: draft.itemLines?.map(((line) => ({
          id: line.id,
          description: line.description ?? "",
          unitPrice: line.unitPrice ?? 0,
          productCode: line.productCode ?? -1,
        }))) ?? [],
        textLines: draft.textLines?.map((line) => ({
          description: line.description,
          id: line.id
        })) ?? [],
        title: draft.title ?? "",
        paymentTermsId: draft.paymentTermsId ?? -1
      })
      newReceivable = storedReceivable
      if (send) {
        const { data: publishedReceivable } = await publishReceivable(taxLegalCaseId, newReceivable.id, newReceivable.paymentTerms?.id ?? -1)
        newReceivable = publishedReceivable
      }
      addReceivable(newReceivable)
      setDraft(emptyTemplate)
      setTemplateId(EMPTY_TEMPLATE_ID)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading('sending_offer', false)
    }
  }

  async function deleteTemplate(templateId: ReceivableId | undefined) {
    try {
      if (!templateId) return;
      toggleLoading('deleting_template', true)
      await deleteReceivableTemplate(templateId)
      const newTemplates = {
        ...templates,
        data: templates.data.filter(item => item.id !== templateId)
      }
      setTemplatesData(newTemplates)
      const nextTemplate = newTemplates?.data?.[0] ?? emptyTemplate
      setTemplateId(nextTemplate?.id ?? EMPTY_TEMPLATE_ID)
      setDraft(nextTemplate)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading('deleting_template', false)
    }
  }

  async function saveTemplate(duplicate?: boolean) {
    try {
      toggleLoading('saving_template', true)
      const payload: UpsertReceivableTemplatePayload = {
        itemLines: draft.itemLines?.map(((line) => ({
          id: line.id,
          description: line.description ?? "",
          unitPrice: line.unitPrice ?? 0,
          productCode: line.productCode ?? -1,
        }))) ?? [],
        textLines: draft.textLines?.map((line) => ({
          description: line.description,
          id: line.id
        })) ?? [],
        title: draft.title ?? ""
      }
      let template: ReceivableTemplate;
      if (!duplicate && templateId && templateId !== EMPTY_TEMPLATE_ID) {
        template = (await updateReceivableTemplate(templateId, payload)).data
        setTemplatesData(prev => ({
          ...prev,
          data: prev.data.map(item => item.id !== template.id ? item : template)
        }))
      } else {
        template = (await storeReceivableTemplate(payload)).data
        setTemplatesData(prev => ({
          ...prev,
          data: [...prev.data, template]
        }))
      }
      setTemplateId(template.id)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading('saving_template', false)
    }
  }


  // NOTE: 27-05-2026 (Nico): It doesnt make sense to preview the offer because the offer has not been created in E-conomic as of this point.
  // In addition, the frondend UI also works as the preview.
  // async function previewPdf(pricing: OfferTotals) {
  //   try {
  //     toggleLoading('downloading_preview', true)
  //     await new Promise(r => setTimeout(r, 400))
  //   } catch (error) {
  //     renderErrorMessage(error)
  //   } finally {
  //     toggleLoading('downloading_preview', false)
  //   }
  // }

  async function downloadOffer(offer: Receivable) {
    try {
      if (!offer.links.offerPdf) return;
      toggleLoading(offer.id, true)
      await openFileLink(offer.links.offerPdf)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(offer.id, false)
    }
  }
  async function sendOffer(offerId: ReceivableId) {
    try {
      const offer = offers.find(offer => offer.id === offerId)
      if (!offer) return;
      toggleLoading(offerId, true)
      const { data } = await publishReceivable(taxLegalCaseId, offer.id, offer.paymentTerms?.id ?? -1)
      replaceReceivable(data)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(offerId, false)
    }
  }
  async function toggleOfferAccepted(offerId: ReceivableId) {
    try {
      toggleLoading(offerId, true)
      const { data } = await acceptReceivable(taxLegalCaseId, offerId)
      replaceReceivable(data)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(offerId, false)
    }
  }
  async function deleteOffer(offerId: ReceivableId) {
    try {
      toggleLoading(offerId, true)
      await deleteReceivable(taxLegalCaseId, offerId, { forceDelete: true })
      removeReceivable(offerId)
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      toggleLoading(offerId, false)
    }
  }

  function onChangePricingItem(id: OfferLineId, partial: Partial<OfferLine>) {

    const defaultText = receivableProducts.find(item => item.code === partial.productCode)?.title
    setItems((prev) => prev.map(item => {
      if (item.id !== id) return item
      return {
        ...item,
        description: defaultText || item.description,
        ...partial
      }
    }))
  }

  function onChangeDescription(id: OfferLineId, text: string) {
    setDraft((prev) => ({
      ...prev,
      textLines: prev.textLines?.map<OfferTextLine>(item => {
        if (item.id !== id) return item;
        return {
          ...item,
          description: text
        }
      })
    }))
  }

  const removePriceItem = (id: OfferLineId) => setItems((prev) => prev.filter(item => item.id !== id))
  function addPriceItem() {
    const id = (draft.itemLines?.length ?? 0) + 1
    const newItem: OfferLine = {
      id: id,
    }
    setItems((prev) => [...prev, newItem])
  }

  const removeDescriptionLine = (id: OfferLineId) => setDraft((prev) => ({ ...prev, textLines: prev.textLines?.filter(item => item.id !== id) }))
  function addDescriptionLine() {
    const id = (draft.textLines?.length ?? 0) + 1
    const newItem: OfferTextLine = {
      id: id,
      description: "",
    }
    setDraft((prev) => ({
      ...prev,
      textLines: [...prev?.textLines ?? [], newItem]
    }))
  }
  function changeOfferPaymentTerm(paymentTermCode: number) {
    if (!isValidNum(paymentTermCode)) return
    setDraft((prev) => ({ ...prev, paymentTermsId: paymentTermCode }))
  }

  return (
    <div className="legal-activity-offer-card">
      <Row gutter={[8, 8]} align="middle">
        <Col flex={1} className="d-flex justify-content-between align-items-center">
          <span className="d-inline-block">{_t("template")}</span>
          <Button disabled={templateId === EMPTY_TEMPLATE_ID} loading={loadingIds.has("deleting_template")} onClick={() => deleteTemplate(templateId)} type="link" danger icon={<Icon name="trash-outline" />} />
        </Col>
        <Col>
          <Select loading={isLoadingTemplates} style={{ width: 250 }}
            options={templateOptions}
            value={templateId} onChange={handleSetTemplate}
          />
        </Col>
      </Row>

      <Row className="mt-05" gutter={[8, 8]} align="middle">
        <Col flex={1}>Titel / {_t("title")}</Col>
        <Col>
          <Input style={{ width: 250 }} value={draft.title} onChange={(e) => setTitle(e.target.value)} />
        </Col>
      </Row>

      <Row className="mt-05" gutter={[8, 8]} align="middle">
        <Col flex={1}> {_t("payment_term")} </Col>
        <Col>
          <ServerSelectBox
            style={{ width: 250 }}
            apiUrl={paymentTermsAutoComplete()}
            allowClear={true}
            showAction={["focus"]}
            value={draft.paymentTermsId}
            onChange={(value) => changeOfferPaymentTerm(value as any as number)}
          />
        </Col>
      </Row>


      <Row style={{ marginTop: 12 }} gutter={[8, 8]}>
        <Col flex={"32px"}></Col>
        <Col flex={"56px"}><small>Varenr.</small></Col>
        <Col flex={1}><small>{_t("description")}</small></Col>
        <Col flex={"140px"}><small>{_t("amount", "vat", " ekskl ")}</small></Col>
      </Row>

      {items?.map(item => {
        return (
          <Row key={item.id} className="mt-03" gutter={[8, 8]} align="middle">
            <Col flex={"32px"}>
              <Button size="small" type="text" onClick={() => removePriceItem(item.id)} icon={<Icon size="medium" name="remove-circle-outline" />} />
            </Col>
            <Col flex={"56px"}>
              <Select
                style={{ width: 48 }}
                loading={isLoadingProducts}
                optionLabelProp="value"
                options={productOptions}
                showAction={["focus"]}
                size="small"
                value={item.productCode}
                dropdownMatchSelectWidth={256}
                onChange={(value) => onChangePricingItem(item.id, { productCode: value })}
              />
            </Col>
            <Col flex={"1 1 0%"}>
              <Input size="small" style={{ minWidth: 0, maxWidth: "100%" }} value={item.description ?? undefined} onChange={(e) => onChangePricingItem(item.id, { description: e.target.value })} />
            </Col>
            <Col flex={"140px"}>
              <InputNum size="small" suffix={"Kr."} value={item.unitPrice} onChange={(val) => onChangePricingItem(item.id, { unitPrice: Number(val ?? "") })} />
            </Col>
          </Row>
        )
      })}

      <Button className="mt-03" size="small" type="link" onClick={addPriceItem}>+ {_t("add", "line")}</Button>

      <div style={{ marginTop: 12 }}>
        {descriptionLines?.map(line => (
          <Row align="middle" className="mt-03 position-relative" gutter={[8, 8]} key={line.id}>
            <Col flex={"32px"}>
              <Button size="small" type="text" onClick={() => removeDescriptionLine(line.id)} icon={<Icon size="medium" name="remove-circle-outline" />} />
            </Col>
            <Col flex={"1 1 0%"}>
              <Input size="small" value={line.description} onChange={(e) => onChangeDescription(line.id, e.target.value)} />
            </Col>
          </Row>
        ))}
        <Row justify="space-between" className="mt-03">
          <Col><Button size="small" type="link" onClick={addDescriptionLine}>+ {_t("add", "descriptionLine")}</Button></Col>
          <Col>
            <Dropdown.Button size="small" loading={loadingIds.has('saving_template')} onClick={() => saveTemplate(false)}
              overlay={
                <Menu items={[
                  {
                    label: _t("save_as_new"),
                    key: "duplicate",
                    disabled: templateId === EMPTY_TEMPLATE_ID,
                    onClick: () => saveTemplate(true)
                  }
                ]} />
              }>
              <Icon name="save-outline" /> {_t(templateId === EMPTY_TEMPLATE_ID ? "create" : "save", "template")}
            </Dropdown.Button>
          </Col>
        </Row>
      </div>

      <div className="border-light border-radius bg-light mt-1" style={{ padding: "8px 16px" }}>
        <Row justify="space-between">
          <Col>{_t("subtotal")}</Col>
          <Col>{format.price(priceResult.subTotal)}</Col>
        </Row>
        <Row justify="space-between" className="mt-03">
          <Col>{_t("vat")} ({format.percent(appConfig("vatRate"))})</Col>
          <Col>{format.price(priceResult.vatAmount)}</Col>
        </Row>
        <DividerLine className="mt-03 mb-0" />
        <Row justify="space-between" className="mt-03 text-semibold">
          <Col>{_t("total")}</Col>
          <Col>{format.price(priceResult.total)}</Col>
        </Row>
      </div>

      <div className="text-right mt-05">
        <Button onClick={() => saveOffer(false)} loading={loadingIds.has('sending_offer')} size="small" icon={<Icon name="save-outline" />}>{_t("save", "offer")}</Button>
        <Popconfirm onConfirm={() => saveOffer(true)} title={_t("msg.confirm_action")}>
          <Button loading={loadingIds.has('sending_offer')} size="small" icon={<Icon name="paper-plane-outline" />} type="primary" className="ml-05">{_t("send", "offer")}</Button>
        </Popconfirm>
      </div>
      <DividerLine className="mt-1 mb-1" />
      <div>
        <Row justify="space-between" className="mb-05">
          <Col>{_t("active_offers")}</Col>
          <Col><small>{offers?.length} {_t("offer")}</small></Col>
        </Row>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {offers?.map(offer => {
            const isDraft = offer.status?.id === DRAFT_ID
            const accepted = offer.status?.id === ACCEPTED_ID
            return (
              <Row key={offer.id} className="bg-light border-light p-05 border-radius " justify="space-between" align="middle">
                <Col style={{ display: "flex", alignItems: "center" }}>
                  <Button disabled={!offer.links.offerPdf}
                    loading={loadingIds.has(offer.id)} size="small"
                    onClick={() => downloadOffer(offer)} type="link"
                    icon={<Icon name="attach-outline" size="large" />} />
                  <div>
                    <Button style={{ display: "block" }} onClick={() => downloadOffer(offer)}
                      disabled={loadingIds.has(offer.id) || !offer.links.offerPdf}
                      type="link" size="small">
                      {offer.title}
                    </Button>
                    <small style={{ marginTop: -4, display: 'block', marginLeft: 8 }}>
                      {format.date(offer.log?.published?.date || offer.createdAt)} &mdash; {offer.itemLines?.length} linjer &mdash; {format.price(offer.totals?.withVat)}
                    </small>
                  </div>
                </Col>
                <Col>
                  <Popconfirm title={_t("msg.confirm_delete")} onConfirm={() => deleteOffer(offer.id)}>
                    <Button
                      loading={loadingIds.has(offer.id)}
                      type={'text'}
                      className="mr-05"
                      size="small" icon={<Icon name="trash-bin-outline" />}
                    />
                  </Popconfirm>

                  {offer.generatingQuote ? (
                    <Tooltip title="Afventer Economic - tilbuddet bliver genereret i Economic">
                      <Button loading size="small">{_t("generating", "offer")}</Button>
                    </Tooltip>
                  ) : (
                    isDraft ? (
                      <Popconfirm title={_t("msg.confirm_action")} onConfirm={() => sendOffer(offer.id)}>
                        <Button
                          loading={loadingIds.has(offer.id)}
                          type={"default"}
                          className={`legal-offer-accept-btn`}
                          size="small" icon={<Icon name="paper-plane-outline" />}
                        >{_t("send", "offer")}</Button>
                      </Popconfirm>
                    ) : (

                      <Popconfirm title={_t("msg.confirm_action")} onConfirm={() => toggleOfferAccepted(offer.id)}>
                        <Button
                          loading={loadingIds.has(offer.id)}
                          type={accepted ? "primary" : "default"}
                          className={`legal-offer-accept-btn ${accepted ? "accepted" : ""}`}
                          size="small" icon={<Icon name="check-circle-bold" />}
                        >{accepted ? _t("accepted") : _t("accept")}</Button>
                      </Popconfirm>
                    )
                  )}
                </Col>
              </Row>
            )
          }
          )}
        </div>
      </div>
    </div>
  )
}

function calculatePrice(pricingItems: OfferLine[], products: ReceivableProduct[] | undefined): OfferTotals {
  let subTotal = 0, vat = 0, total = 0;

  pricingItems.forEach(item => {
    const product = products?.find(p => p.code === item.productCode)
    const vatFree = !!product?.vatExempt;
    const price = item.unitPrice ?? 0;
    const amountPrice = price
    subTotal += amountPrice
    vat += vatFree ? 0 : amountPrice * appConfig("vatRate")
  })
  total = subTotal + vat;
  return { subTotal, vatAmount: vat, total }
}
